/**
 * Carrier for the `Sign-In Completed` event's `method` (`password` | `google` |
 * `magic_link`).
 *
 * The method is only known at the `AuthContext` login functions, but `track()`
 * can't fire until `identify()` runs — a render later, in the identify
 * lifecycle. So the login function RECORDS the method and the lifecycle CONSUMES
 * it once the gate opens.
 *
 * Uses `sessionStorage` (not a module var) because the magic-link flow does a
 * hard reload after login, which would wipe an in-memory value before the
 * post-reload identify could read it. It's per-tab (no cross-session bleed) and
 * holds only the method enum, never a token/email/PII.
 */

import type { ClientEventProps } from './events'

export type SignInMethod = ClientEventProps['Sign-In Completed']['method']

const STORAGE_KEY = 'orchard.analytics.signInMethod'

const VALID_METHODS: readonly SignInMethod[] = ['password', 'google', 'magic_link']

function isSignInMethod(value: unknown): value is SignInMethod {
  return typeof value === 'string' && (VALID_METHODS as readonly string[]).includes(value)
}

/**
 * Record the method used for an in-progress sign-in. Called by the three
 * `AuthContext` login functions on a successful token exchange. Survives the
 * magic-link hard reload via `sessionStorage`. Never throws (storage may be
 * unavailable, e.g. privacy mode) — analytics must never break auth.
 */
export function recordSignInMethod(method: SignInMethod): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, method)
  } catch {
    // sessionStorage can throw (disabled / quota). Swallow: the worst case is a
    // single un-attributed Sign-In Completed event, never a broken login.
  }
}

/**
 * Read and clear the recorded sign-in method. Called once by the identify
 * lifecycle right after `identify()` opens the no-track gate. Returns `null`
 * when no sign-in is pending (e.g. a silent session restore on app re-init or a
 * token refresh) — so the event fires ONLY on an actual sign-in action, never
 * on a session resume. Clears on read so it cannot double-fire. Never throws.
 */
export function consumeSignInMethod(): SignInMethod | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (raw === null) {
      return null
    }
    window.sessionStorage.removeItem(STORAGE_KEY)
    return isSignInMethod(raw) ? raw : null
  } catch {
    return null
  }
}
